'use client';

import { Children, useEffect, useRef } from 'react';

import {
  FormInput as AriakitFormInput,
  FormError,
  FormLabel,
  FormSubmit,
  useFormContext,
  useStoreState,
} from '@ariakit/react';
import { useGSAP } from '@gsap/react';
import cx from 'classnames';
import { gsap } from 'gsap';
import { Flip } from 'gsap/Flip';

import Loading from '~/ui/assets/icons/loading.svg';
import { Icon } from '~/ui/components/icon';
import { invariant } from '~/utils/invariant';

import styles from './formInput.module.scss';

gsap.registerPlugin(Flip, useGSAP);

export interface FormInputProps extends React.ComponentPropsWithoutRef<'input'> {
  className?: string;
  fieldName: string;
  inputLabel: string;
  submitLabel?: string;
  disableSubmit?: boolean;
  isPending?: boolean;
  validationRegex?: string;
  validationError?: string;
}

export function FormInput({
  className,
  fieldName,
  inputLabel,
  submitLabel,
  disableSubmit,
  isPending,
  validationRegex,
  validationError,
  ...props
}: FormInputProps): React.ReactElement {
  const form = useFormContext();
  invariant(form, 'FormInput must be wrapped in FormProvider');

  const labelRef = useRef<HTMLLabelElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const labelWrapperRef = useRef<HTMLDivElement>(null);
  const inputValue = useStoreState(form, state => state.values[fieldName]);

  const validateInput = () => {
    if (inputValue && validationRegex) {
      const isMatch = new RegExp(validationRegex).test(inputValue);
      if (!isMatch) {
        form.setError(fieldName, validationError || 'Enter a valid value');
      }
    }
  };

  useEffect(() => {
    // The input component somehow retains invalid values that were cleared
    // from the form when submitting.
    // This ensures it is set to the form's input value on initial render.
    if (inputRef.current) {
      inputRef.current.value = inputValue || '';
    }
  }, [inputValue]);

  useGSAP(() => {
    const labelWrapper = labelWrapperRef.current;
    const label = labelRef.current;
    if (!labelWrapper || !label) {
      return;
    }

    const state = Flip.getState(label, {
      props: 'fontFamily,fontSize,fontWeight,lineHeight,opacity,transform',
    });
    if (!!inputValue) {
      labelWrapper.classList.add(styles.active);
    } else {
      labelWrapper.classList.remove(styles.active);
    }

    Flip.from(state, {
      duration: 0.3,
      ease: 'power2.out',
    });
  }, [inputValue]);

  return (
    <div className={styles.root}>
      <div className={cx(styles.inputWrapper, className)}>
        <div className={cx(styles.labelWrapper, styles.active)} ref={labelWrapperRef}>
          <FormLabel className={styles.label} name={fieldName} ref={labelRef}>
            {inputLabel}
          </FormLabel>
        </div>
        <AriakitFormInput
          className={styles.input}
          name={fieldName}
          onChange={() => form.setError(fieldName, undefined)}
          onBlur={() => validateInput()}
          ref={inputRef}
          {...props}
        />
        {!disableSubmit ? (
          isPending ? (
            <Loading className={styles.loading} aria-label="Loading" />
          ) : (
            <FormSubmit className={styles.submit}>
              {submitLabel && <>{submitLabel}&nbsp;</>}
              <Icon name="expanding-arrow-right" />
            </FormSubmit>
          )
        ) : null}
      </div>

      <FormError
        name={fieldName}
        render={({ className, children, ...props }) =>
          Children.count(children) > 0 && (
            <div className={cx(styles.error, className)} {...props}>
              <svg
                width="18"
                height="18"
                viewBox="0 0 18 18"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
                aria-hidden
              >
                <rect
                  x="0.75"
                  y="0.75"
                  width="16.5"
                  height="16.5"
                  rx="8.25"
                  stroke="currentcolor"
                  strokeWidth="1.5"
                />
                <path
                  fillRule="evenodd"
                  clipRule="evenodd"
                  d="M8.25 10.5601L8.25 3.73315L9.75 3.73315L9.75 10.5601L8.25 10.5601Z"
                  fill="currentcolor"
                />
                <path
                  fillRule="evenodd"
                  clipRule="evenodd"
                  d="M8.25 14.2668L8.25 12.5601L9.75 12.5601L9.75 14.2668L8.25 14.2668Z"
                  fill="currentcolor"
                />
              </svg>
              {children}
            </div>
          )
        }
      />
    </div>
  );
}
